import { getPreferences } from "@/api/preferences";
import { appConfig } from "@/config/app";
import { useStaticAuth } from "@/contexts/StaticAuthProvider";
import {
  ThemeSettings,
  UIPreferences,
  UserPreferences,
} from "@/types/ApiResponses/UserPreferences";
import { CacheUpdater, createRecordOps } from "@/utils/queryCache";
import { useQuery, useQueryClient } from "@tanstack/react-query";

const preferencesQueryKeys = {
  userPrefs: "user-preferences",
};

export const usePreferences = <TData = UserPreferences>(
  select?: (data: UserPreferences) => TData
) => {
  const authData = useStaticAuth();
  return useQuery({
    queryKey: [preferencesQueryKeys.userPrefs],
    queryFn: getPreferences,
    initialData: authData.prefs,
    select,
  });
};

export const getThemeSettings = (
  prefs: UserPreferences | undefined
): ThemeSettings => {
  if (prefs) {
    return {
      mode: prefs.themeMode,
      color: prefs.themeColor,
    };
  }

  return {
    mode: appConfig.defaultThemeMode,
    color: appConfig.defaultThemeColor,
  };
};

export const getUIPreferences = (
  prefs: UserPreferences | undefined
): UIPreferences => {
  if (prefs) {
    return {
      showQuickDeposit: prefs.showQuickDeposit,
    };
  }

  return {
    showQuickDeposit: appConfig.showQuickDeposit,
  };
};

export const useThemeSettings = (): ThemeSettings => {
  const query = usePreferences(getThemeSettings);
  return query.data;
};

export const useUIPreferences = (): UIPreferences => {
  const query = usePreferences(getUIPreferences);
  return query.data;
};

interface PreferencesManager {
  get: () => UserPreferences | undefined;
  replace: (data: UserPreferences) => void;
  patch: (updater: CacheUpdater<UserPreferences>) => void;
  invalidate: () => void;
}

export const usePreferencesManager = (): PreferencesManager => {
  const queryClient = useQueryClient();
  const ops = createRecordOps<UserPreferences>(queryClient, [
    preferencesQueryKeys.userPrefs,
  ]);

  return {
    get: () => ops.get(),
    replace: (data) => ops.replace(data),
    patch: (updater) => ops.patch(updater),
    invalidate: () => ops.invalidate(),
  };
};
